import { BodyText, Button, Container, FormControl, Heading, Textarea } from '@farol-ds/react'
import { breakpointMd, breakpointSm } from '@farol-ds/tokens'
import { useMetrics } from '@jusbrasil-web/docview/core'
import { useOnScreen, useViewportSize } from '@jusbrasil-web/shared-hooks'
import { buildWhatsappRedirect } from '@jusbrasil-web/shared-seus-direitos'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { useEffect, useMemo, useRef, useState } from 'react'

import { getUserAgentProperties } from '../../utils/user-agent'
import styles from './orientation-box.module.scss'

export function OrientationBox() {
  const { ref, sendMetrics, metricsProperties } = useMetrics<HTMLDivElement>()
  const session = useUserSession()
  const [question, setQuestion] = useState('')
  const [hasError, setHasError] = useState(false)
  const CTA_TEXT = 'Solicitar orientação'

  // sendmetric ref to avoid re-render
  const sendMetricRef = useRef(sendMetrics)
  const ctaRef = useRef<HTMLButtonElement>(null)
  const metricSent = useRef<boolean>(false)

  const userAgentProps = getUserAgentProperties()

  const { width } = useViewportSize({
    delay: 0,
    initialWidth: 0,
    initialHeight: 0,
  })

  const anchorCompensation = width < breakpointSm ? 64 : width < breakpointMd ? 77 : 0

  const isCtaOnScreen = useOnScreen(
    ctaRef,
    { threshold: 1, rootMargin: `0px 0px -${anchorCompensation}px 0px` },
    false
  )

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHasError(false)
    setQuestion(e.target.value)
  }

  const metadata = useMemo(
    () => ({
      copy: CTA_TEXT,
      page: 'docview-artigo',
      action: 'send_question',
      position: 'comments',
      doc_id: metricsProperties.doc_id,
    }),
    [metricsProperties.doc_id]
  )

  const sendQuestion = (question: string) => {
    sendMetrics('SeusDireitos.CtaClick', {
      app_id: 'web-docview',
      page: metadata.page,
      browser: userAgentProps.browser,
      os: userAgentProps.os,
      user_agent: userAgentProps.user_agent,
      dvce_type: userAgentProps.dvce_type,
      dvce_ismobile: userAgentProps.dvce_ismobile,
      device_name: userAgentProps.device_name,
      metadata,
    })

    if (!question.length) {
      setHasError(true)
      return
    }

    sendMetrics('SeusDireitos.QuestionSent', {
      app_id: 'web-docview',
      page: metadata.page,
      browser: userAgentProps.browser,
      os: userAgentProps.os,
      user_agent: userAgentProps.user_agent,
      dvce_type: userAgentProps.dvce_type,
      dvce_ismobile: userAgentProps.dvce_ismobile,
      device_name: userAgentProps.device_name,
      metadata: { ...metadata, question },
    })

    const url = buildWhatsappRedirect({
      message: question,
      deviceId: session?.jdid,
      userId: session?.pid != null ? String(session.pid) : undefined,
    })
    window.open(url, '_blank')
  }

  useEffect(() => {
    if (isCtaOnScreen && !metricSent.current) {
      sendMetricRef.current('SeusDireitos.CtaView', {
        app_id: 'web-docview',
        page: metadata.page,
        browser: userAgentProps.browser,
        os: userAgentProps.os,
        user_agent: userAgentProps.user_agent,
        dvce_type: userAgentProps.dvce_type,
        dvce_ismobile: userAgentProps.dvce_ismobile,
        device_name: userAgentProps.device_name,
        metadata,
      })

      metricSent.current = true
    }
  }, [isCtaOnScreen, metadata, userAgentProps])

  return (
    <Container ref={ref} type="fluid" className={styles.container}>
      <Heading size="lg">Envie as suas dúvidas</Heading>

      <BodyText size={'md'}>
        Receba orientações iniciais e entenda o que fazer no seu caso.
      </BodyText>

      <FormControl className={styles.textareaContainer} error={hasError}>
        <Textarea
          placeholder="Descreva o que aconteceu e o que você gostaria de saber..."
          data-hj-allow="true"
          rows={5}
          wrap="soft"
          value={question}
          onChange={onChange}
          error={hasError}
        />
        <FormControl.ErrorMessage>
          Por favor, preencha o seu relato para prosseguir
        </FormControl.ErrorMessage>
      </FormControl>

      <div className={styles.buttonRow}>
        <Button
          ref={ctaRef}
          size={'md'}
          kind="tertiary"
          className={styles.button}
          onClick={() => sendQuestion(question)}
          block
        >
          {CTA_TEXT}
        </Button>
      </div>
    </Container>
  )
}
